import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ColumnHeaderFilterIcon from '../../common/ColumnHeaderFilterIcon';
import type { IndividualDetailsRow, DRIValueType } from '../../../types/driInsights';
import { SEARCE_COLORS } from '../../../theme';
import type { DataTableColumnDef } from '../../table/dataTableTypes';
import { driValueForProductDetail } from '../../../utils/driInsightsUtils';
import { renderRoundedNumberCell } from '../../common/numericTableCells';

function IndividualDetailsHeaderLabel(props: {
  label: string;
  filterColumnId: string;
  useCheckboxFilter?: boolean;
  isNumericFilter?: boolean;
  onFilterClick: (
    event: React.MouseEvent<HTMLElement>,
    columnId: string,
    useCheckbox?: boolean,
    isNumeric?: boolean
  ) => void;
  hasActiveFilter: (columnId: string) => boolean;
  centerHeader?: boolean;
}) {
  const {
    label,
    filterColumnId,
    useCheckboxFilter = false,
    isNumericFilter = false,
    onFilterClick,
    hasActiveFilter,
    centerHeader,
  } = props;
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: 34,
        textAlign: centerHeader ? 'center' : 'left',
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          verticalAlign: 'middle',
          maxWidth: '100%',
          ...(centerHeader ? { justifyContent: 'center', width: '100%' } : {}),
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            lineHeight: 1.25,
            color: SEARCE_COLORS.white,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onFilterClick(e, filterColumnId, useCheckboxFilter, isNumericFilter);
            }}
            sx={{
              color: hasActiveFilter(filterColumnId) ? SEARCE_COLORS.white : alpha(SEARCE_COLORS.white, 0.75),
              padding: 0.5,
              '&:hover': { backgroundColor: alpha(SEARCE_COLORS.white, 0.2) },
            }}
          >
            <ColumnHeaderFilterIcon isFilterActive={hasActiveFilter(filterColumnId)} fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export interface BuildIndividualDetailsDataTableColumnsParams {
  gridColumns: string[];
  gridCellValueType: DRIValueType;
  onSort: (columnId: string) => void;
  onFilterClick: (
    event: React.MouseEvent<HTMLElement>,
    columnId: string,
    useCheckbox?: boolean,
    isNumeric?: boolean
  ) => void;
  hasActiveFilter: (columnId: string) => boolean;
}

const emailStickyHeaderSx = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 12,
};

const emailStickyBodySx = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 4,
  backgroundColor: SEARCE_COLORS.white,
};

const drpIdStickyHeaderSx = {
  position: 'sticky' as const,
  left: 250, // Starts exactly where the 250px Email column ends
  zIndex: 12,
  boxShadow: `inset -2px 0 0 ${SEARCE_COLORS.white}`,
};

const drpIdStickyBodySx = {
  position: 'sticky' as const,
  left: 250, // Starts exactly where the 250px Email column ends
  zIndex: 4,
  backgroundColor: SEARCE_COLORS.white,
  boxShadow: `inset -2px 0 0 ${SEARCE_COLORS.neutral_200}`, // Boundary line separating the frozen section
};

export function buildIndividualDetailsDataTableColumns(
  p: BuildIndividualDetailsDataTableColumnsParams
): DataTableColumnDef<IndividualDetailsRow>[] {
  const { gridColumns, gridCellValueType, onSort, onFilterClick, hasActiveFilter } = p;

  const emailColumn: DataTableColumnDef<IndividualDetailsRow> = {
    id: 'individual_email',
    header: (
      <IndividualDetailsHeaderLabel
        label="Email"
        filterColumnId="individual_email"
        useCheckboxFilter
        onFilterClick={onFilterClick}
        hasActiveFilter={hasActiveFilter}
      />
    ),
    meta: {
      columnMenuLabel: 'Email',
      hideable: false,
      width: 250,
      minWidth: 250,
      maxWidth: 250,
      headerCellProps: {
        onClick: () => onSort('individual_email'),
        sx: { cursor: 'pointer' },
      },
      headerCellSx: emailStickyHeaderSx,
      cellSx: {
        ...emailStickyBodySx,
        fontWeight: 500,
        fontSize: '0.75rem',
      },
    },
    cell: ({ row }) => (
      <Typography variant="body2" component="span" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
        {row.employee.member_email}
      </Typography>
    ),
  };

  const drpIdColumn: DataTableColumnDef<IndividualDetailsRow> = {
    id: 'drp_id',
    header: (
      <IndividualDetailsHeaderLabel
        label="DRP ID"
        filterColumnId="drp_id"
        useCheckboxFilter
        onFilterClick={onFilterClick}
        hasActiveFilter={hasActiveFilter}
      />
    ),
    meta: {
      columnMenuLabel: 'DRP ID',
      hideable: false,
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      headerCellProps: {
        onClick: () => onSort('drp_id'),
        sx: { cursor: 'pointer' },
      },
      headerCellSx: drpIdStickyHeaderSx,
      cellSx: {
        ...drpIdStickyBodySx,
        fontWeight: 500,
        fontSize: '0.75rem',
      },
    },
    cell: ({ row }) => (
      <Typography variant="body2" component="span" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
        {row.employee.drp_id || '—'}
      </Typography>
    ),
  };

  const productColumns: DataTableColumnDef<IndividualDetailsRow>[] = gridColumns.map((product) => {
    const colId = `product_${product}`;
    return {
      id: colId,
      header: (
        <IndividualDetailsHeaderLabel
          label={product}
          filterColumnId={colId}
          isNumericFilter
          onFilterClick={onFilterClick}
          hasActiveFilter={hasActiveFilter}
          centerHeader
        />
      ),
      meta: {
        columnMenuLabel: product,
        align: 'center' as const,
        minWidth: 160,
        headerCellProps: {
          onClick: () => onSort(colId),
          sx: { cursor: 'pointer' },
        },
      },
      cell: ({ row }) => {
        const prodData = row.products[product];
        if (!prodData) {
          return (
            <Typography
              variant="body2"
              component="span"
              sx={{ fontSize: '0.75rem', fontWeight: 400, color: SEARCE_COLORS.textSecondary }}
            >
              —
            </Typography>
          );
        }
        const v = driValueForProductDetail(prodData, gridCellValueType);
        return renderRoundedNumberCell(v, 2);
      },
    };
  });

  return [emailColumn, drpIdColumn, ...productColumns];
}